import {
  DealerShowroom,
  DealerShowroomPhone,
} from '../api/types/dealerApiTypes';
import type { DealerShowroomSeo } from '../api/types/dealerApiTypes';
import { dealerApi } from '../api/dealerApi';
import { error, ok, Result } from 'domains/Result';
import { Dealer } from 'domains/Dealer';
import { AxiosResponse } from 'axios';
import { createNotification, Notification } from 'domains/Notification';
import {
  IGetAdsRequest,
  ISearchPageAds,
  ISearchPageFilter,
  ISearchPagePaging,
} from '../api/types/searchPageApiTypes';
import { IncomingHttpHeaders } from 'http';
import { getToday, isCurrentlyOpen } from 'helpers/openingHours';

export interface ShowroomDataSource {
  getDealerShowroomFilters: (
    dealerId: string,
    section: string,
  ) => Promise<AxiosResponse<ISearchPageFilter[]>>;
  getDealerShowroomByName: (
    dealerName: string,
    payload: IGetAdsRequest,
    headers?: IncomingHttpHeaders,
  ) => Promise<AxiosResponse<DealerShowroom>>;
  getDealerShowroomPhone: (
    dealerId: string,
    headers?: IncomingHttpHeaders,
  ) => Promise<AxiosResponse<DealerShowroomPhone>>;
  getDealerShowroomSeo: (
    dealerId: string,
  ) => Promise<AxiosResponse<DealerShowroomSeo>>;
}

interface ShowroomRepository {
  getDealerShowroomFilters: (
    dealerName: string,
    section: string,
  ) => Promise<
    Result<
      Error,
      {
        filters: ISearchPageFilter[];
      }
    >
  >;
  getDealerShowroom: (
    dealerName: string,
    payload: IGetAdsRequest,
    headers?: IncomingHttpHeaders,
  ) => Promise<
    Result<
      Error,
      {
        dealer: Dealer;
        notification: Notification;
        ads: ISearchPageAds[];
        paging: ISearchPagePaging;
      }
    >
  >;
  getDealerShowroomPhone: (
    dealerName: string,
    headers?: IncomingHttpHeaders,
  ) => Promise<
    Result<
      Error,
      {
        phone: string;
      }
    >
  >;
  getDealerShowroomSeo: (dealerName: string) => Promise<
    Result<
      Error,
      {
        seoDisplayName: string;
        seoCategoryDisplayName: string;
      }
    >
  >;
}

const mapShowroomDealer = (
  dealerSlug: string,
  dealerShowroom: DealerShowroom,
): Dealer => {
  const weekday = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  let {
    id,
    name,
    email,
    enhancedAddress,
    logo,
    forecourtPhoto,
    googleReview,
    enhancedOpeningHours,
    franchisesDisplay,
    latitude,
    longitude,
    address,
    description,
    googleMapPhoto,
    websiteURL,
    totalAds,
  } = dealerShowroom.dealer;

  const hasAppointmentOnly =
    enhancedOpeningHours &&
    enhancedOpeningHours.days &&
    enhancedOpeningHours.days.filter((item) => item.appointmentOnly).length ==
      7;

  const workingHours = enhancedOpeningHours
    ? enhancedOpeningHours.days.map((item) => ({
        day: item.day,
        openTime: item.openTime,
        closeTime: item.closeTime,
        appointmentOnly: Boolean(item.appointmentOnly),
        appointmentOnlyText: item.appointmentOnlyText || '',
        today: getToday().toISOString(),
        isCurrentOpen:
          Boolean(item.day === weekday[getToday().getDay()]) &&
          isCurrentlyOpen(item.openTime, item.closeTime),
      }))
    : null;

  return {
    id: id.toString(),
    slug: dealerSlug,
    name: name,
    email: email,
    description: description,
    info: franchisesDisplay,
    address: enhancedAddress
      ? enhancedAddress.addressLocality
        ? `${enhancedAddress.addressLocality}, ${enhancedAddress.addressRegion}`
        : `${enhancedAddress.addressRegion}`
      : address,
    fullAddress: address,
    logo: logo?.small || null,
    logoMedium: logo?.medium || null,
    backgroundImage: forecourtPhoto?.large || null,
    backgroundImageSmall: forecourtPhoto?.small || null,
    rating:
      googleReview?.rating && googleReview.displayReviews
        ? Number(googleReview.rating)
        : null,
    googleReview:
      googleReview && googleReview.displayReviews ? googleReview : null,
    region: enhancedAddress.addressRegion,
    workingHours,
    googleMapsLink: `https://www.google.com/maps/place/${latitude},${longitude}/@${latitude},${longitude}z/`,
    googleMapPhoto:
      googleMapPhoto && googleMapPhoto.small ? googleMapPhoto.small : null,
    ratingTotal: googleReview ? Number(googleReview.userRatingsTotal) : null,
    website: websiteURL || '',
    hasAppointmentOnly: !!hasAppointmentOnly,
    stock: totalAds || null,
    trusted: dealerShowroom.dealer?.trustedDealer || null,
    sections: null,
  };
};

const ShowroomRepository = (
  showroomDataSource: ShowroomDataSource,
): ShowroomRepository => {
  const getDealerShowroom: ShowroomRepository['getDealerShowroom'] = async (
    dealerName: string,
    payload: IGetAdsRequest,
    httpHeaders: IncomingHttpHeaders,
  ) => {
    try {
      const { data, headers } =
        await showroomDataSource.getDealerShowroomByName(
          dealerName,
          payload,
          httpHeaders,
        );
      return ok({
        dealer: mapShowroomDealer(dealerName, data),
        notification: createNotification(
          headers.totalunread,
          headers.unreadhistorychecks,
          headers.unreadmessages,
        ),
        ads: data.ads,
        paging: data.paging,
      });
    } catch (err) {
      return error(new Error(err));
    }
  };

  const getDealerShowroomPhone: ShowroomRepository['getDealerShowroomPhone'] =
    async (dealerId: string) => {
      try {
        const { data } = await showroomDataSource.getDealerShowroomPhone(
          dealerId,
        );
        return ok({
          phone: data.phone,
        });
      } catch (err) {
        return error(new Error(err));
      }
    };

  const getDealerShowroomFilters: ShowroomRepository['getDealerShowroomFilters'] =
    async (dealerId: string, section: string) => {
      try {
        const { data } = await showroomDataSource.getDealerShowroomFilters(
          dealerId,
          section,
        );
        return ok({
          filters: data,
        });
      } catch (err) {
        return error(new Error(err));
      }
    };

  const getDealerShowroomSeo: ShowroomRepository['getDealerShowroomSeo'] =
    async (dealerId: string) => {
      try {
        const { data } = await showroomDataSource.getDealerShowroomSeo(
          dealerId,
        );
        return ok({
          seoDisplayName: data.seoDisplayName,
          seoCategoryDisplayName: data.seoCategoryDisplayName,
        });
      } catch (err) {
        return error(new Error(err));
      }
    };

  return {
    getDealerShowroom,
    getDealerShowroomPhone,
    getDealerShowroomFilters,
    getDealerShowroomSeo,
  };
};

export { ShowroomRepository };

export const showroomRepository = ShowroomRepository({
  getDealerShowroomFilters: dealerApi.getDealerShowroomFilters,
  getDealerShowroomByName: dealerApi.getDealerShowroomByName,
  getDealerShowroomPhone: dealerApi.getDealerShowroomPhone,
  getDealerShowroomSeo: dealerApi.getDealerShowroomSeo,
});
